import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { getAuthenticatedLocalClient } from "@/lib/supabase-clients";

export type AppRole = "super_admin" | "admin" | "am" | "rm" | "client" | "owner" | "client_test" | "owner_test";

function getLocalUserIdFromToken(localToken: string): string | null {
  try {
    const payload = JSON.parse(atob(localToken.split('.')[1]));
    return payload.sub || null;
  } catch {
    return null;
  }
}

export function useUserRole() {
  const { user, localToken } = useAuth();

  const localUserId = localToken ? getLocalUserIdFromToken(localToken) : null;

  const { data: roles = [], isLoading } = useQuery({
    queryKey: ["user_roles", localUserId],
    queryFn: async () => {
      const client = getAuthenticatedLocalClient();
      if (!client || !localUserId) return [];

      const { data, error } = await client
        .from("user_roles")
        .select("role")
        .eq("user_id", localUserId);
      if (error) {
        console.error("[useUserRole] Query error:", error);
        throw error;
      }
      return (data || []).map((r: any) => r.role as AppRole);
    },
    enabled: !!user && !!localToken && !!localUserId,
    staleTime: 5 * 60 * 1000,
  });

  const isSuperAdmin = roles.includes("super_admin");
  const isAdmin = isSuperAdmin || roles.includes("admin");
  const primaryRole: AppRole | null = isSuperAdmin
    ? "super_admin"
    : roles.includes("admin")
    ? "admin"
    : roles.includes("am")
    ? "am"
    : roles.includes("rm")
    ? "rm"
    : roles.includes("client")
    ? "client"
    : roles.includes("client_test")
    ? "client_test"
    : roles.includes("owner")
    ? "owner"
    : roles.includes("owner_test")
    ? "owner_test"
    : null;

  return { roles, primaryRole, isSuperAdmin, isAdmin, isLoading };
}
